import styled from "styled-components";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  text-align: center;
  padding: 0 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at 30% 40%,
      rgba(59, 130, 246, 0.12) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 70% 60%,
      rgba(139, 92, 246, 0.08) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const Contents = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  position: relative;
  z-index: 1;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(59, 130, 246, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  color: #93c5fd;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 16px;
  }
`;

const Title = styled.h1`
  font-size: 64px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -1px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 40px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const SubTitle = styled.p`
  font-size: 24px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const Tagline = styled.p`
  font-size: 17px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    max-width: 280px;
    gap: 12px;
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  @media (max-width: 480px) {
    justify-content: center;
    padding: 14px 24px;
  }
`;

const AppStoreButton = styled(DownloadButton)`
  background: #ffffff;
  color: #0f172a;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.15);
  }
`;

const PlayStoreButton = styled(DownloadButton)`
  background: transparent;
  color: #ffffff;
  border: 1.5px solid rgba(255, 255, 255, 0.3);

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 255, 255, 0.08);
  }
`;

const ButtonLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
`;

const ButtonSmallText = styled.span`
  font-size: 11px;
  font-weight: 400;
  opacity: 0.7;
`;

const ButtonMainText = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const IconSvg = styled.svg`
  flex-shrink: 0;
`;

const AppleIcon = () => (
  <IconSvg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </IconSvg>
);

const PlayIcon = () => (
  <IconSvg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 010 1.38l-2.302 2.302L15.396 12l2.302-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z" />
  </IconSvg>
);

const TitleSection = () => {
  return (
    <Container>
      <Contents>
        <Badge>Location-Based Social Network</Badge>
        <Title>Following Moments</Title>
        <SubTitle>장소를 따라, 순간을 기록하다</SubTitle>
        <Tagline>Your journey, beautifully captured.</Tagline>
        <ButtonGroup>
          <AppStoreButton
            href="https://apps.apple.com/us/app/lone/id6757098843"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon />
            <ButtonLabel>
              <ButtonSmallText>Download on the</ButtonSmallText>
              <ButtonMainText>App Store</ButtonMainText>
            </ButtonLabel>
          </AppStoreButton>
          <PlayStoreButton
            href="https://play.google.com/store/apps/details?id=com.followingmoments.lone&hl=ko"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PlayIcon />
            <ButtonLabel>
              <ButtonSmallText>GET IT ON</ButtonSmallText>
              <ButtonMainText>Google Play</ButtonMainText>
            </ButtonLabel>
          </PlayStoreButton>
        </ButtonGroup>
      </Contents>
    </Container>
  );
};

export default TitleSection;
